import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Download, Trash2, LogOut, Loader2, AlertTriangle, 
  Crown, Calendar, CreditCard, RefreshCw, Check, X 
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { 
  exportAccountData, deleteAccount, getSubscriptionStatus,
  cancelSubscription, reactivateSubscription 
} from '../services/api';

const Account = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Load subscription status
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const status = await getSubscriptionStatus();
        setSubscription(status);
      } catch (err) {
        console.error('Error loading subscription:', err);
      } finally {
        setLoadingSubscription(false);
      }
    };
    
    if (isAuthenticated) {
      loadSubscription();
    } else {
      setLoadingSubscription(false);
    }
  }, [isAuthenticated]);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    try {
      await exportAccountData();
    } catch (err) {
      setError('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteAccount();
      await logout();
      navigate('/');
    } catch (err) {
      setError('Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    setError(null);
    try {
      const result = await cancelSubscription();
      setSubscription(prev => ({
        ...prev,
        cancel_at_period_end: true,
        current_period_end: result.current_period_end
      }));
    } catch (err) {
      setError('Failed to cancel subscription. Please try again.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    setError(null);
    try {
      await reactivateSubscription();
      setSubscription(prev => ({
        ...prev,
        cancel_at_period_end: false
      }));
    } catch (err) {
      setError('Failed to reactivate subscription. Please try again.');
    } finally {
      setReactivating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-[#F3F6F4]">
      {/* Header */}
      <header className="border-b border-[#CEDACF]/50 bg-[#F3F6F4]/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div 
            className="text-xl sm:text-2xl font-semibold text-[#2F5F4F] cursor-pointer" 
            style={{fontFamily: 'DM Serif Display, serif'}}
            onClick={() => navigate('/')}
          >
            OHMNAI
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#2F5F4F]/70 hover:text-[#2F5F4F] hover:bg-[#EAF4EC]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h1 className="text-2xl sm:text-3xl text-[#2F5F4F] mb-8" style={{fontFamily: 'DM Serif Display, serif'}}>
          Account Settings
        </h1>

        {/* User Info */}
        {isAuthenticated && user && (
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-[#CEDACF]/30 shadow-sm mb-6">
            <h2 className="text-lg font-medium text-[#2F5F4F] mb-4">Your Account</h2>
            <div className="flex items-center gap-4">
              {user.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-16 h-16 rounded-full border-2 border-[#B2D3C2]"
                />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-[#2F5F4F]">{user.name}</p>
                  {subscription?.is_member && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#2F5F4F] text-white text-xs rounded-full">
                      <Crown className="h-3 w-3" />
                      Circle
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#2F5F4F]/60">{user.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Management */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-[#CEDACF]/30 shadow-sm mb-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="h-5 w-5 text-[#2F5F4F]" />
            <h2 className="text-lg font-medium text-[#2F5F4F]">Subscription</h2>
          </div>

          {loadingSubscription ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-[#2F5F4F]" />
            </div>
          ) : subscription?.is_member ? (
            <div className="space-y-4">
              {/* Active subscription */}
              <div className={`flex items-center justify-between p-4 rounded-xl border ${
                subscription.status === 'past_due' 
                  ? 'bg-red-50/50 border-red-200/50'
                  : 'bg-[#EAF4EC]/50 border-[#B2D3C2]/50'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    subscription.status === 'past_due' ? 'bg-red-500' : 'bg-[#2F5F4F]'
                  }`}>
                    <Crown className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2F5F4F]">OHMNAI Circle</p>
                    <p className="text-sm text-[#2F5F4F]/60">$9/month</p>
                  </div>
                </div>
                <div className="text-right">
                  {subscription.status === 'past_due' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                      <X className="h-3 w-3" />
                      Payment Issue
                    </span>
                  ) : subscription.cancel_at_period_end ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                      <X className="h-3 w-3" />
                      Cancelling
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#EAF4EC] text-[#2F5F4F] text-xs rounded-full">
                      <Check className="h-3 w-3" />
                      Active
                    </span>
                  )}
                </div>
              </div>

              {/* Payment issue warning */}
              {subscription.status === 'past_due' && (
                <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                  <p className="text-sm text-red-700">
                    We couldn't process your last payment. Please update your payment method to continue your membership.
                  </p>
                </div>
              )}

              {/* Billing period */}
              <div className="flex items-center gap-2 text-sm text-[#2F5F4F]/70">
                <Calendar className="h-4 w-4" />
                <span>
                  {subscription.cancel_at_period_end 
                    ? `Access until ${formatDate(subscription.current_period_end)}`
                    : `Next billing date: ${formatDate(subscription.current_period_end)}`
                  }
                </span>
              </div>

              {/* Actions */}
              <div className="pt-4 border-t border-[#CEDACF]/30">
                {subscription.cancel_at_period_end ? (
                  <Button
                    onClick={handleReactivate}
                    disabled={reactivating}
                    className="bg-[#2F5F4F] text-white hover:bg-[#2F5F4F]/90"
                  >
                    {reactivating ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Reactivate Subscription
                  </Button>
                ) : (
                  <Button
                    onClick={handleCancelSubscription}
                    disabled={cancellingSubscription}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    {cancellingSubscription ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Cancel Subscription
                  </Button>
                )}
                <p className="text-xs text-[#2F5F4F]/50 mt-2">
                  {subscription.cancel_at_period_end 
                    ? "Your subscription will end at the current period. Reactivate to continue."
                    : "You can cancel anytime. Access continues until the end of your billing period."
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[#2F5F4F]/70">
                You're currently on the free plan.
              </p>
              <Button
                onClick={() => navigate('/circle')}
                className="bg-[#2F5F4F] text-white hover:bg-[#2F5F4F]/90"
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade to Circle
              </Button>
            </div>
          )}
        </div>

        {/* Data & Privacy */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-[#CEDACF]/30 shadow-sm mb-6">
          <h2 className="text-lg font-medium text-[#2F5F4F] mb-2">Your Data</h2>
          <p className="text-[#2F5F4F]/60 mb-6 text-sm">
            OHMNAI believes in transparency. You own your data, always.
          </p>

          <div className="space-y-4">
            {/* Export */}
            <div className="flex items-center justify-between p-4 bg-[#EAF4EC]/30 rounded-xl border border-[#B2D3C2]/30">
              <div>
                <h3 className="font-medium text-[#2F5F4F]">Export Data</h3>
                <p className="text-sm text-[#2F5F4F]/60">Download all your data as JSON</p>
              </div>
              <Button
                onClick={handleExport}
                disabled={isExporting}
                variant="outline"
                className="border-[#CEDACF] text-[#2F5F4F] hover:bg-[#EAF4EC]"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <><Download className="h-4 w-4 mr-2" /> Export</>
                )}
              </Button>
            </div>

            {/* Delete */}
            <div className="flex items-center justify-between p-4 bg-red-50/50 rounded-xl border border-red-200/50">
              <div>
                <h3 className="font-medium text-[#2F5F4F]">Delete Account</h3>
                <p className="text-sm text-[#2F5F4F]/60">Permanently remove all your data</p>
              </div>
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Session */}
        {isAuthenticated && (
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-[#CEDACF]/30 shadow-sm">
            <h2 className="text-lg font-medium text-[#2F5F4F] mb-4">Session</h2>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#CEDACF] text-[#2F5F4F] hover:bg-[#EAF4EC]"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign Out
            </Button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 rounded-xl border border-red-200">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-medium text-[#2F5F4F]">Delete Account?</h3>
              </div>
              <p className="text-[#2F5F4F]/70 mb-6">
                This will permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  variant="outline"
                  className="flex-1 border-[#CEDACF] text-[#2F5F4F] hover:bg-[#EAF4EC]"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Delete Forever'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Account;
